import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axiosInstance from "../../../AxiosInstance";
import {
  ToastTypes,
  toast,
} from "../../../components/ajonjolib/toasts/toast/toast";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "../../../App";

function BillerSettingsPage() {
  const [showModal, setShowModal] = useState(true);
  const [primaryColor, setPrimaryColor] = useState("#000000");
  const [secondaryColor, setSecondaryColor] = useState("#000000");
  const [billerData, setBillerData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { id } = useParams();

  const navigate = useNavigate();

  const { state } = useLocation();

  const billerQuery = useQuery({
    queryKey: ["biller", id],
    queryFn: () => axiosInstance.get(`suppliers/${id}/website-config/`),
    enabled: !!id,
  });

  // console.log({ billerQuery: billerQuery?.data?.data });

  useEffect(() => {
    if (billerQuery?.data?.data?.id) {
      const data = billerQuery.data.data;
      setBillerData(data);
      setPrimaryColor(data.primary_color || "#000000");
      setSecondaryColor(data.secondary_color || "#000000");
    }
  }, [billerQuery?.data]);

  const handleClose = () => {
    setShowModal(false);
    navigate(-1);
  };

  const handleCustomizeSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      let response;

      if (billerQuery?.data?.data?.id) {
        response = await axiosInstance.patch(
          `suppliers/website-configs/${billerQuery?.data?.data?.domaine_name}/`,
          formData
        );
        toast("Modifications enregistrées avec succès", ToastTypes.SUCCESS);
      } else {
        response = await axiosInstance.post(
          "suppliers/website-configs/",
          formData
        );
        toast("Configuration créée avec succès", ToastTypes.SUCCESS);
      }
      queryClient.invalidateQueries({ queryKey: ["tableData-biller-list"] });
      handleClose();
    } catch (error) {
      toast(
        error.response?.data?.message || "Une erreur est survenue",
        ToastTypes.ERROR
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showModal) return null;

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-end"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1050,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className={`h-100 bg-white shadow-lg`}
        style={{
          width: window.innerWidth <= 768 ? "100%" : "400px",
          animation: "slideIn 0.3s ease-out",
        }}
      >
        <div className="h-100 d-flex flex-column">
          {/* Header */}
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4
              className="m-0 fw-bold"
              style={{
                fontSize: window.innerWidth <= 768 ? "1rem" : "1rem",
              }}
            >
              {billerQuery?.data?.data?.id
                ? "Modifier la configuration de "
                : "Nouvelle configuration pour "}
              <u>{state?.data?.name}</u>
            </h4>
            <button
              className="btn btn-icon"
              onClick={handleClose}
              style={{ border: "none", background: "none" }}
            >
              <FontAwesomeIcon
                icon={faXmark}
                size={window.innerWidth <= 768 ? "1x" : "lg"}
              />
            </button>
          </div>

          {/* Content */}
          <div className="p-3 p-md-4 flex-grow-1" style={{ overflowY: "auto" }}>
            {billerQuery?.data?.data?.id && billerQuery.isLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            ) : (
              <form
                id="customizeForm"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  if (!billerQuery?.data?.data?.id) {
                    formData.append("supplier", id);
                  }

                  if (formData.get("is_website_active")) {
                    formData.append("is_website_active", true);
                  } else {
                    formData.append("is_website_active", false);
                  }

                  if (formData.get("show_email_on_website")) {
                    formData.append("show_email_on_website", true);
                  } else {
                    formData.append("show_email_on_website", false);
                  }

                  if (formData.get("show_phone_on_website")) {
                    formData.append("show_phone_on_website", true);
                  } else {
                    formData.append("show_phone_on_website", false);
                  }

                  // verification domaine_name obligatoires et des chose a respecter (qui ne doit pas avoir des caracteres speciaux)
                  if (!formData.get("domaine_name")) {
                    toast(
                      "Le nom de domaine est obligatoire",
                      ToastTypes.ERROR
                    );
                    return;
                  }

                  if (formData.get("domaine_name").includes(" ")) {
                    toast(
                      "Le nom de domaine ne doit pas avoir des espaces",
                      ToastTypes.ERROR
                    );
                    return;
                  }

                  // verifier les expressions regulieres mais peut contenir des points et des tirets et des underscores, pas de majuscules
                  const subdomainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

                  function isValidSubdomain(subdomain) {
                    return subdomainRegex.test(subdomain);
                  }

                  if (!isValidSubdomain(formData.get("domaine_name"))) {
                    toast(
                      "Le nom de domaine ne doit pas avoir des caracteres speciaux et ne doit pas contenir de majuscules",
                      ToastTypes.ERROR
                    );
                    return;
                  }

                  handleCustomizeSubmit(formData);
                }}
              >
                <div className="mb-4">
                  <div className="mb-3 text-start">
                    <label className="form-label small d-block text-start fw-bold">
                      Personnalisation du site web
                    </label>
                  </div>
                  <div className="p-3 bg-light rounded-3">
                    {/* Couleurs */}
                    <div
                      className={`${
                        window.innerWidth <= 768 ? "d-block" : "d-flex gap-3"
                      } mb-3`}
                    >
                      <div
                        className={`${window.innerWidth <= 768 ? "mb-3" : ""}`}
                        style={{ flex: 1, textAlign: "left" }}
                      >
                        <label className="form-label small d-block text-start">
                          Couleur principale
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            type="text"
                            className="form-control text-start"
                            name="primary_color"
                            placeholder="#000000"
                            value={primaryColor}
                            pattern="^#([A-Fa-f0-9]{6})$"
                            onChange={(e) => {
                              setPrimaryColor(e.target.value);
                            }}
                          />
                          <input
                            type="color"
                            className="form-control form-control-color"
                            style={{ width: "40px", padding: "0" }}
                            value={primaryColor}
                            onChange={(e) => {
                              setPrimaryColor(e.target.value);
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ flex: 1, textAlign: "left" }}>
                        <label className="form-label small d-block text-start">
                          Couleur secondaire
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            type="text"
                            className="form-control text-start"
                            name="secondary_color"
                            placeholder="#000000"
                            value={secondaryColor}
                            pattern="^#([A-Fa-f0-9]{6})$"
                            onChange={(e) => {
                              setSecondaryColor(e.target.value);
                            }}
                          />
                          <input
                            type="color"
                            className="form-control form-control-color"
                            style={{ width: "40px", padding: "0" }}
                            value={secondaryColor}
                            onChange={(e) => {
                              setSecondaryColor(e.target.value);
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Description d'en-tête */}
                    <div className="mb-3 text-start">
                      <label className="form-label small d-block text-start">
                        Description d'en-tête
                      </label>
                      <textarea
                        className="form-control text-start"
                        name="header_description"
                        rows="2"
                        placeholder="Description qui apparaîtra dans l'en-tête"
                        defaultValue={billerData?.header_description}
                      ></textarea>
                    </div>

                    {/* Nom de domaine */}
                    <div className="mb-3 text-start">
                      <label className="form-label small d-block text-start">
                        Nom de domaine
                      </label>
                      <input
                        type="text"
                        className="form-control text-start"
                        name="domaine_name"
                        placeholder="example"
                        defaultValue={billerData?.domaine_name}
                        required
                      />
                    </div>

                    <div className="mb-3 text-start">
                      <label className="form-label small d-block text-start fw-bold">
                        Favicon du site web
                      </label>
                      {billerData?.favicon?.image && (
                        <div
                          style={{
                            width: "60px",
                            height: "60px",
                            marginBottom: "10px",
                          }}
                        >
                          <img
                            src={billerData?.favicon?.image}
                            alt="favicon"
                            // className="img-fluid"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control text-start"
                        name="favicon"
                        placeholder="0"
                        defaultValue={billerData?.favicon}
                        accept="image/png, image/jpg"
                      />
                    </div>

                    {/* Site web actif */}
                    {billerQuery?.isLoading ? null : billerQuery?.data?.data
                        ?.id ? (
                      <div className="form-check text-start mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="is_website_active"
                          name="is_website_active"
                          defaultChecked={
                            billerQuery?.data?.data?.id
                              ? billerData?.is_website_active
                              : true
                          }
                        />
                        <label
                          className="form-check-label text-start"
                          htmlFor="is_website_active"
                        >
                          Site web actif
                        </label>
                      </div>
                    ) : (
                      <div className="form-check text-start mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="is_website_active"
                          name="is_website_active"
                          defaultChecked={true}
                        />
                        <label
                          className="form-check-label text-start"
                          htmlFor="is_website_active"
                        >
                          Site web actif
                        </label>
                      </div>
                    )}

                    {/* Checkbox pour voir le mail de contact */}
                    <div className="form-check text-start mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="show_email_on_website"
                        name="show_email_on_website"
                        defaultChecked={
                          billerQuery?.data?.data?.id
                            ? billerData?.show_email_on_website
                            : true
                        }
                      />
                      <label
                        className="form-check-label text-start"
                        htmlFor="show_email_on_website"
                      >
                        Afficher le mail de contact
                      </label>
                    </div>

                    {/* Checkbox pour voir le telephone de contact */}
                    <div className="form-check text-start">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="show_phone_on_website"
                        name="show_phone_on_website"
                        defaultChecked={
                          billerQuery?.data?.data?.id
                            ? billerData?.show_phone_on_website
                            : true
                        }
                      />
                      <label
                        className="form-check-label text-start"
                        htmlFor="show_phone_on_website"
                      >
                        Afficher le telephone de contact
                      </label>
                    </div>
                  </div>

                  {/* Logo */}
                  <div className="p-3 bg-light rounded-3 mt-3">
                    <div className="mb-3 text-start">
                      <label className="form-label small d-block text-start fw-bold">
                        Parametres de la boutique
                      </label>
                    </div>

                    <div className="mb-3 text-start">
                      <label className="form-label small d-block text-start fw-bold">
                        Commission
                      </label>
                      <input
                        type="number"
                        className="form-control text-start"
                        name="commission"
                        placeholder="0"
                        defaultValue={billerData?.commission}
                        min={0}
                        max={100}
                      />
                    </div>

                    <div className="mb-3 text-start">
                      <label className="form-label small d-block text-start fw-bold">
                        Logo par defaut des produits
                      </label>
                      {billerData?.default_product_image?.image && (
                        <div
                          style={{
                            width: "60px",
                            height: "60px",
                            marginBottom: "10px",
                          }}
                        >
                          <img
                            src={billerData?.default_product_image?.image}
                            alt="default_product_image"
                            // className="img-fluid"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control text-start"
                        name="default_product_image"
                        placeholder="0"
                        defaultValue={billerData?.default_product_image?.image}
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                      />
                    </div>

                    <div className="mb-3 text-start">
                      <label className="form-label small d-block text-start fw-bold">
                        Logo par defaut des categories
                      </label>
                      {billerData?.default_category_image?.image && (
                        <div
                          style={{
                            width: "60px",
                            height: "60px",
                            marginBottom: "10px",
                          }}
                        >
                          <img
                            src={billerData?.default_category_image?.image}
                            alt="default_category_image"
                            // className="img-fluid"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control text-start"
                        name="default_category_image"
                        placeholder="0"
                        defaultValue={billerData?.default_category_image?.image}
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                      />
                    </div>
                  </div>
                  {/* Parametres d'affichage des composants du site web */}
                  {/* <div className="p-3 bg-light rounded-3 mt-3">
                    <div className="mb-3 text-start">
                      <label className="form-label small d-block text-start fw-bold">
                        Styles d'affichage
                      </label>

                      <div className="mb-3">
                        <label
                          className="form-label small"
                          htmlFor="header_style"
                        >
                          Style de l'en-tête
                        </label>
                        <select
                          className="form-select"
                          id="header_style"
                          // name="header_style"
                          defaultValue={billerData?.header_style || "default"}
                        >
                          <option value="default">Par defaut</option>
                          <option value="centered">secondaire</option>
                          <option value="minimal">Minimal - Logo uniquement</option>
                          <option value="full-width">Pleine largeur avec mega-menu</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label
                          className="form-label small"
                          htmlFor="footer_style"
                        >
                          Style du pied de page
                        </label>
                        <select
                          className="form-select"
                          id="footer_style"
                          // name="footer_style"
                          defaultValue={billerData?.footer_style || "default"}
                        >
                          <option value="default">Par defaut</option>
                          <option value="simple">Simple</option>
                          <option value="minimal">Minimal</option>
                          <option value="modern">Moderne</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label
                          className="form-label small"
                          htmlFor="product_list_style"
                        >
                          Style de la liste des produits
                        </label>
                        <select
                          className="form-select"
                          id="product_list_style"
                          // name="product_list_style"
                          defaultValue={
                            billerData?.product_list_style || "default"
                          }
                        >
                          <option value="default">Par defaut</option>
                          <option value="grid">Grille</option>
                          <option value="list">Liste</option>
                          <option value="compact">Compact</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label
                          className="form-label small"
                          htmlFor="category_style"
                        >
                          Style des catégories
                        </label>
                        <select
                          className="form-select"
                          id="category_style"
                          // name="category_style"
                          defaultValue={billerData?.category_style || "default"}
                        >
                          <option value="default">Par defaut</option>
                          <option value="sidebar">Barre latérale</option>
                          <option value="top-menu">Menu horizontal</option>
                          <option value="dropdown">Menu déroulant</option>
                          <option value="mega-menu">Mega menu</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label
                          className="form-label small"
                          htmlFor="search_style"
                        >
                          Style de la recherche
                        </label>
                        <select
                          className="form-select"
                          id="search_style"
                          // name="search_style"
                          defaultValue={billerData?.search_style || "default"}
                        >
                          <option value="default">Par defaut</option>
                          <option value="expanded">Étendu</option>
                          <option value="overlay">Superposition</option>
                          <option value="minimal">Icône</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label
                          className="form-label small"
                          htmlFor="cart_style"
                        >
                          Style du panier
                        </label>
                        <select
                          className="form-select"
                          id="cart_style"
                          // name="cart_style"
                          defaultValue={billerData?.cart_style || "default"}
                        >
                          <option value="default">Par defaut</option>
                          <option value="sidebar">Barre latérale</option>
                          <option value="page">Page dédiée</option>
                          <option value="mini">Mini panier</option>
                        </select>
                      </div>
                    </div>
                  </div> */}
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-3 p-md-4 border-top">
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-light flex-grow-1"
                onClick={handleClose}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary flex-grow-1"
                form="customizeForm"
                disabled={billerQuery.isLoading || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Chargement...
                  </>
                ) : billerQuery?.data?.data?.id ? (
                  "Enregistrer"
                ) : (
                  "Créer"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BillerSettingsPage;
